(ns frontend.view.nev.surveys.form.status-prefilled
  (:require
   [clojure.string :as string]
   [common.localization :as loc]
   [common.util :as cu]
   [frontend.state.current-session :as session]
   [frontend.view.nev.surveys.form.components :as components]
   [frontend.view.nev.surveys.form.events :as events]
   [frontend.view.nev.surveys.form.sub :as sub]
   [re-frame.core :as rf]
   [ui.components.button.view :as button]
   [ui.components.radio-group.view :as radio-group]
   [ui.editors.labelled-input.view :as labelled-input]
   [ui.util :as uu]))

(defn auth-type-selector
  [val]
  (let [sms-allowed? (session/survey-sms-auth?)]
    [radio-group/view
     {:value     val
      :options   [{:value :auth-type/link
                   :class (uu/cm "border-solid border p-2 rounded"
                                 (when (= val :auth-type/link)
                                   "border-primary-regular-border"))
                   :label [:div
                           {:class "flex flex-col gap-1 text-left"}
                           [:span
                            {:class "block pb-1 text-bold"}
                            (loc/tr :survey-auth-type-label-link)]
                           [:p
                            {:class "m-0 text-sm"}
                            (loc/tr :survey-auth-type-text-link)]]}
                  {:value :auth-type/email
                   :class (uu/cm "border-solid border p-2 rounded"
                                 (when (= val :auth-type/link)
                                   "border-primary-regular-border"))
                   :label [:div
                           {:class "flex flex-col gap-1 text-left"}
                           [:span
                            {:class "block pb-1 text-bold"}
                            (loc/tr :survey-auth-type-label-email)]
                           [:p
                            {:class "m-0 text-sm"}
                            (loc/tr :survey-auth-type-text-email)]]}
                  {:value     :auth-type/email-or-sms
                   :class     (uu/cm "border-solid border p-2 rounded"
                                     (when (= val :auth-type/link)
                                       "border-primary-regular-border")
                                     (when (not sms-allowed?)
                                       "bg-disabled-light"))
                   :disabled? (not sms-allowed?)
                   :tooltip   (when-not sms-allowed?
                                (loc/tr :survey-auth-no-sms-tooltip))
                   :label     [:div
                               {:class "flex flex-col gap-1 text-left"}
                               [:span
                                {:class "block pb-1 text-bold"}
                                (loc/tr :survey-auth-type-label-email-or-sms)]
                               [:p
                                {:class "m-0 text-sm"}
                                (loc/tr :survey-auth-type-text-email-or-sms)]]}]
      :on-change (fn [v]
                   (rf/dispatch [::events/set-auth-type v]))}]))

(defn auth-sharing-selector
  [process]
  (let [options (remove nil?
                        [{:value :sharing-type/not-allowed
                          :label (loc/tr :sharing-type/not-allowed)}
                         #_{:value :sharing-type/inviters-only
                            :label (loc/tr :sharing-type/inviters-only)}
                         #_{:value :sharing-type/same-domain-only
                            :label (loc/tr :sharing-type/same-domain-only)}
                         #_{:value :sharing-type/free-sharing
                            :label (loc/tr :sharing-type/free-sharing)}])]
    [:<>
     [labelled-input/view
      {:editor-type :editor.type/select
       :id          "survey-sharing-select"
       :label       (loc/tr :survey-link-sharing-type)
       :value       (->> options
                         (cu/filter-by :value (:survey-process/sharing-type process))
                         (first))
       :placeholder (loc/tr :select-sharing-type)
       :on-change   #(rf/dispatch [::events/set-sharing-type %])
       :options     options}]]))

(defn link-auth
  []
  (let [survey-url @(rf/subscribe [::sub/survey-link])]
    [:div
     [:a
      {:class "flex items-center gap-2 text-base-light-text hover:text-base-light-text-accent"
       :href  survey-url}
      "Link to survey"
      [:i.fas.fa-external-link-alt]]]))

(defn invitees-list
  [process]
  (let [prefilled-state @(rf/subscribe [::sub/prefilled-state])
        auth-type (:survey-process/auth-type prefilled-state)]
    [:<>
     [:ul
      {:class "flex flex-col gap-3"}
      (->> process
           :survey-process/invitees
           (map-indexed
            (fn [idx {:keys [email phone]}]
              ^{:key (str "survey-invitee-" idx)}
              [:li
               {:class "p-3 flex flex-col gap-2 bg-base-regular rounded relative"}
               [:div
                {:class "flex flex-col gap-3"}
                [labelled-input/view
                 {:label     (loc/tr :survey-invitee-email)
                  :data-type :data.type/email
                  :value     email
                  :on-change #(rf/dispatch [::events/set-auth-email idx %])}]

                (when (not= auth-type :auth-type/link)
                  [:<>
                   [labelled-input/view
                    {:label     (loc/tr :survey-invitee-phone)
                     :data-type :data.type/phone
                     :value     (if-not (string/starts-with? (str phone) "+")
                                  (str "+" phone)
                                  phone)
                     :on-change #(rf/dispatch [::events/set-auth-phone idx %])}]])]
               [:div
                {:class "absolute top-2 right-2"}
                [button/square-secondary-open
                 {:tooltip    (loc/tr :survey-block-editor/remove-invitee)
                  :aria-label (loc/tr :survey-block-editor/remove-invitee)
                  :on-click   #(rf/dispatch [::events/remove-invitee idx])}
                 [:i.fal.fa-times-circle]]]]))
           (doall))]
     [:div
      {}
      [button/sm-primary-solid
       {:on-click #(rf/dispatch [::events/add-invitee])}
       [:i.fal.fa-plus]
       (loc/tr :add-survey-invitee-button)]]]))

(defn view
  []
  (let [prefilled-state @(rf/subscribe [::sub/prefilled-state])
        auth-type (:survey-process/auth-type prefilled-state)
        sharing-type (:survey-process/sharing-type prefilled-state)
        unsaved-changes @(rf/subscribe [::sub/unsaved-prefilled-changes])
        valid-data? @(rf/subscribe [::sub/valid-prefilled-data?])]
    [:div
     {:class "flex flex-col flex-1 overflow-hidden"}
     [:div
      {:class "flex flex-col flex-1 overflow-y-auto"}
      [:div
       [:div
        {:class "py-3 flex flex-auto flex-col overflow-auto"}
        [:div
         {:class "py-4 flex flex-col flex-1 container gap-2 max-w-[1024px] text-base-light-text"}
         [:div
          {:class "flex flex-col gap-3"}
          [auth-type-selector auth-type]

          (when (some #{auth-type}
                      [:auth-type/link
                       :auth-type/email-or-sms
                       :auth-type/email
                       :auth-type/sms])
            [invitees-list prefilled-state])
          (when (and (= auth-type :auth-type/link)
                     (not unsaved-changes))
            [link-auth prefilled-state])]]
        #_[:div
           (pr-str unsaved-changes) " -> " (cu/blank-value? unsaved-changes)
           [:div
            (pr-str prefilled-state)]
           [:br]
           [:div
            (pr-str @(rf/subscribe [::sub/survey-process]))]

           ]]]]
     [:div
      {:class "shadow-sm"}
      [:div
       {:class "p-3 mx-auto max-w-[1024px] flex justify-between gap-2"}
       [:div
        {:class "flex gap-3"}
        (if-not unsaved-changes
          [components/editor-exit-button]
          [components/editor-exit-without-saving-button])]
       [:div
        {:class "flex gap-3"}
        [components/editor-save-button (or (not unsaved-changes)
                                           (not valid-data?)) [::events/save-prefilled]]
        [components/editor-next-button unsaved-changes [::events/submit-prefilled]]]]]
     ]))

